import { cn } from '@/lib/utils';
import { Bell, User, Search, HelpCircle, Settings, LogOut, UserCircle, Zap } from 'lucide-react';
import { useEffect, useState } from 'react';
import { FosBadge } from '@/components/ui/flipperos';
import { useTour } from '@/components/onboarding/flipperos';
import { NotificationDropdown } from '@/components/notifications/NotificationDropdown';
import { useNotifications } from '@/hooks/useNotifications';
import { Link, useNavigate } from 'react-router';
import { useEnabledAutomationCount } from '@/hooks/useAutomationRules';
import { supabase } from '@/integrations/supabase/client';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export interface FosHeaderProps {
  onOpenCommand: () => void;
  /** Optional title for the page (sidebar nav already conveys location too). */
  title?: string;
}

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

export function FosHeader({ onOpenCommand, title }: FosHeaderProps) {
  const { data: enabledAutomations = 0 } = useEnabledAutomationCount();
  // Refresh the cmdk hint key if window unfocuses (purely visual)
  const [_hover, setHover] = useState(false);
  useEffect(() => {
    setHover(false);
  }, []);
  const { startTour } = useTour();
  const navigate = useNavigate();
  const { unreadCount } = useNotifications();

  async function handleSignOut() {
    await supabase.auth.signOut();
    navigate('/auth');
  }

  return (
    <header
      className={cn(
        'flex items-center h-14 px-4 gap-3',
        'bg-fos-bg-surface border-b border-fos-border',
        'flex-shrink-0',
      )}
    >
      {title ? (
        <h1 className="truncate text-[15px] font-semibold text-fos-text-primary">{title}</h1>
      ) : null}

      {/* Search trigger that opens the ⌘K palette. We don't bind a real input
          here — the actual filter happens inside the modal. */}
      <button
        type="button"
        onClick={onOpenCommand}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        data-tour-target="header-search"
        className={cn(
          'flex items-center gap-2 h-8 px-2.5 flex-1 max-w-full sm:max-w-[420px]',
          'bg-fos-bg-base border border-fos-border rounded-fos',
          'text-fos-text-muted text-[13px] text-left',
          'transition-colors duration-fos hover:border-fos-border-strong',
        )}
        aria-label="Open command palette"
      >
        <Search size={14} className="flex-shrink-0" />
        <span className="flex-1 truncate">Search or jump to...</span>
        <kbd
          className={cn(
            'flex items-center gap-0.5 h-5 px-1.5 rounded-fos-sm',
            'bg-fos-bg-elevated border border-fos-border',
            'font-mono text-[10px] text-fos-text-secondary',
          )}
        >
          {isMac ? '⌘' : 'Ctrl'}+K
        </kbd>
      </button>

      <div className="flex-1" aria-hidden />

      {/* Read-only automations status (fl-8q6). Replaces a Copilot/Autonomous
          toggle that only wrote a local preference: agents act through the
          automation rules a user switches on, so show that and link to them. */}
      <Link
        to="/automation"
        data-tour-target="header-ai-mode"
        title="Agents act only through automation rules you switch on. Every action is logged under Agents."
        className={cn(
          'flex h-8 items-center gap-1.5 rounded-fos border border-fos-border bg-fos-bg-base px-3',
          'text-[12px] font-medium text-fos-text-secondary hover:text-fos-text-primary',
          'transition-colors duration-fos',
        )}
      >
        <Zap size={13} className={enabledAutomations ? 'text-fos-accent' : 'text-fos-text-muted'} />
        {enabledAutomations
          ? `${enabledAutomations} automation${enabledAutomations === 1 ? '' : 's'} on`
          : 'Automations off'}
      </Link>

      <button
        type="button"
        aria-label="Take a tour"
        title="Take the welcome tour. Use ⌘K → 'Take a tour' for page-specific tours."
        onClick={() => startTour('welcome', { restart: true })}
        data-tour-target="header-help"
        className={cn(
          'h-8 w-8 flex items-center justify-center rounded-fos',
          'text-fos-text-secondary hover:text-fos-text-primary hover:bg-fos-bg-elevated',
          'transition-colors duration-fos',
        )}
      >
        <HelpCircle size={16} />
      </button>

      <NotificationDropdown>
        <button
          type="button"
          aria-label="Notifications"
          className={cn(
            'relative h-8 w-8 flex items-center justify-center rounded-fos',
            'text-fos-text-secondary hover:text-fos-text-primary hover:bg-fos-bg-elevated',
            'transition-colors duration-fos',
          )}
        >
          <Bell size={16} />
          {unreadCount > 0 && (
            <FosBadge
              variant="accent"
              className="absolute -right-0.5 -top-0.5 h-2 w-2 border-fos-bg-surface p-0"
              dot
            >
              {''}
            </FosBadge>
          )}
        </button>
      </NotificationDropdown>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="User menu"
            className={cn(
              'h-8 w-8 flex items-center justify-center rounded-full',
              'bg-fos-bg-elevated border border-fos-border text-fos-text-secondary',
              'hover:bg-fos-bg-hover hover:text-fos-text-primary',
              'transition-colors duration-fos',
            )}
          >
            <User size={14} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuLabel>My Account</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate('/settings')}>
            <Settings className="mr-2 h-4 w-4" />
            Settings
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate('/settings')}>
            <UserCircle className="mr-2 h-4 w-4" />
            Profile
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleSignOut} className="text-destructive">
            <LogOut className="mr-2 h-4 w-4" />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
